// DynamicComparisonTable.tsx - Fetches Excel files dynamically

import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './DynamicComparisonTable.css';

interface ComparisonTableProps {
  fileUrl?: string;
  title?: string;
  className?: string;
  onError?: (error: string) => void;
}

interface ComparisonData {
  headers: string[];
  sections: ComparisonSection[];
}

interface ComparisonSection {
  title: string;
  features: ComparisonFeature[];
}

interface ComparisonFeature {
  name: string;
  values: string[];
}

const DynamicComparisonTable: React.FC<ComparisonTableProps> = ({ 
  fileUrl = '/data/shop-tiers.xlsx', // 👈 DEFAULT FILE LOCATION
  title = "Comparison Table",
  className = "",
  onError
}) => {
  const [data, setData] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
	fetchAndParseExcel();
  }, [fileUrl]);

  const fetchAndParseExcel = async () => {
	try {
	  setLoading(true);
	  setError(null);

	  // Fetch Excel file
	  const response = await fetch(fileUrl);
	  if (!response.ok) {
		throw new Error(`Failed to fetch file: ${response.statusText}`);
	  }

	  const arrayBuffer = await response.arrayBuffer();
	  
	  // Parse Excel with SheetJS
	  const workbook = XLSX.read(arrayBuffer, {
		cellStyles: true,
		cellFormula: true,
		cellDates: true,
		cellNF: true,
		sheetStubs: true
	  });

	  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
	  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

	  // Convert to comparison format
	  const comparisonData = parseExcelData(jsonData);
	  setData(comparisonData);

	} catch (err) {
	  const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
	  setError(errorMessage);
	  onError?.(errorMessage);
	  console.error('Error fetching comparison data:', err);
	} finally {
	  setLoading(false);
	}
  };

  const parseExcelData = (excelData: string[][]): ComparisonData => {
	if (excelData.length === 0) {
	  throw new Error('Excel file is empty');
	}

	const headers = excelData[0];
	const dataRows = excelData.slice(1).filter(row => row.length > 0);
	
	const sections: ComparisonSection[] = [];
	let currentSection: ComparisonSection | null = null;
	
	dataRows.forEach((row) => {
	  if ((row.length === 1 && row[0] && row[0].trim()) || 
		  (!row[0] && row[1] && row[1].trim())) {
		// This is a section header - either old format (single cell) or new format (empty first column)
		if (currentSection && currentSection.features.length > 0) {
		  sections.push(currentSection);
		}
		currentSection = {
		  title: row[0] ? row[0].trim() : row[1].trim(), // Use first non-empty cell as title
		  features: []
		};
	  } else if (row.length >= headers.length && row[0] && row[0].trim()) {
		// This is a feature row
		if (!currentSection) {
		  currentSection = {
			title: "General",
			features: []
		  };
		}
		
		currentSection.features.push({
		  name: row[0].trim(),
		  values: row.slice(1).map(val => val?.toString().trim() || '-')
		});
	  }
	});
	
	// Add the last section
if (currentSection && typeof currentSection === 'object' && 'features' in currentSection && Array.isArray((currentSection as any).features) && (currentSection as any).features.length > 0) {
	  sections.push(currentSection);
	}
	
	return { headers, sections };
  };

  const refreshData = () => {
	fetchAndParseExcel();
  };

  if (loading) {
	return (
	  <div className={`comparison-table-container ${className}`}>
		<div className="comparison-loading">
		  <div className="loading-spinner"></div>
		  <p>Loading comparison data...</p>
		</div>
	  </div>
	);
  }

  if (error) {
	return (
	  <div className={`comparison-table-container ${className}`}>
		<div className="comparison-error">
		  <h3>Error Loading Comparison Table</h3>
		  <p>{error}</p>
		  <button className="button body" onClick={refreshData}>
			<span>Try Again</span>
		  </button>
		</div>
	  </div>
	);
  }

  if (!data) {
	return null;
  }

  return (
	<div className={`comparison-table-container ${className}`}>
	  {title && <h2 className="comparison-title">{title}</h2>}
	  
	  <div className="comparison-refresh">
		<button 
		  className="refresh-button" 
		  onClick={refreshData}
		  title="Refresh data"
		>
		  🔄
		</button>
	  </div>
	  
	  <div className="comparison-table-wrapper">
		<table className="comparison-table">
		  {/* Table Header */}
		  <thead>
			<tr>
			  <th className="feature-header">Specificații</th>
			  {data.headers.slice(1).map((header, index) => (
				<th key={index} className={`tier-header tier-${index + 1}`}>
				  {header}
				</th>
			  ))}
			</tr>
		  </thead>
		  
		  {/* Table Body */}
		  <tbody>
			{data.sections.map((section, sectionIndex) => (
			  <React.Fragment key={sectionIndex}>
				{/* Section Header */}
				<tr className="section-header">
				  <td className="section-title-blank"></td>
				  <td colSpan={data.headers.length - 1} className="section-title">
					{section.title}
				  </td>
				</tr>
				
				{/* Section Features */}
				{section.features.map((feature, featureIndex) => (
				  <tr key={featureIndex} className="feature-row">
					<td className="feature-name">{feature.name}</td>
					{feature.values.map((value, valueIndex) => (
					  <td key={valueIndex} className={`feature-value tier-${valueIndex + 1}`}>
						{value === 'Da' ? (
						  <span className="check-icon">✓</span>
						) : value === '-' ? (
						  <span className="cross-icon">✗</span>
						) : value === 'Nelimitat' ? (
						  <span className="unlimited">∞</span>
						) : (
						  value
						)}
					  </td>
					))}
				  </tr>
				))}
			  </React.Fragment>
			))}
		  </tbody>
		</table>
	  </div>
	  
	  <div className="comparison-meta">
		<small>Last updated: {new Date().toLocaleString()}</small>
	  </div>
	</div>
  );
};

export default DynamicComparisonTable;